<template>
    <div class="relative" ref="searchContainer">
        <form @submit.prevent="handleSearch" class="relative" role="search">
            <label :for="inputId" class="sr-only">Пошук книг і хештегів</label>
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg class="h-5 w-5 text-foxy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </div>
            <input
                :id="inputId"
                v-model="searchQuery"
                type="text"
                role="combobox"
                aria-autocomplete="list"
                :aria-expanded="showSuggestions ? 'true' : 'false'"
                :aria-controls="listboxId"
                :aria-activedescendant="activeDescendant"
                :aria-busy="loading ? 'true' : 'false'"
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @keydown.arrow-down.prevent="navigateDown"
                @keydown.arrow-up.prevent="navigateUp"
                @keydown.enter.prevent.stop="selectSuggestion"
                @keydown.escape.prevent="hideSuggestions"
                class="foxy-input block w-full rounded-lg !py-2 !pl-10 !pr-20"
                :placeholder="placeholder"
                autocomplete="off"
            />
            <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                <button type="submit" class="text-sm font-medium text-foxy-action transition-colors hover:text-foxy-action/80">
                    Пошук
                </button>
            </div>
        </form>

        <div
            v-if="showSuggestions"
            :id="listboxId"
            ref="suggestionsList"
            role="listbox"
            aria-label="Результати пошуку"
            class="foxy-panel absolute z-50 mt-1 max-h-96 w-full overflow-y-auto rounded-lg shadow-xl"
        >
            <div v-if="loading" class="p-3 text-sm foxy-muted" role="status" aria-live="polite">Пошук…</div>

            <template v-else-if="explicitHashtagMode">
                <div class="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide foxy-muted" role="presentation">Хештеги</div>
                <template v-if="visibleHashtagSuggestions.length > 0">
                    <button
                        v-for="(hashtag, index) in visibleHashtagSuggestions"
                        :id="optionId(index)"
                        :key="`hashtag-${hashtag.id}`"
                        type="button"
                        role="option"
                        :aria-selected="selectedIndex === index ? 'true' : 'false'"
                        @mousedown.prevent
                        @click="selectHashtag(hashtag)"
                        @mouseenter="selectedIndex = index"
                        :class="[
                            'block w-full cursor-pointer px-3 py-3 text-left text-sm font-semibold text-foxy-text transition-colors hover:bg-foxy-elevated',
                            selectedIndex === index ? 'bg-foxy-elevated' : '',
                        ]"
                    >
                        #{{ hashtag.name }}
                    </button>
                </template>
                <div v-else class="p-3 text-sm foxy-muted" role="status">Хештегів не знайдено</div>
            </template>

            <template v-else-if="hasAnyNormalResults">
                <div class="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide foxy-muted" role="presentation">Книги</div>
                <template v-if="visibleBookSuggestions.length > 0">
                    <button
                        v-for="(book, index) in visibleBookSuggestions"
                        :id="optionId(index)"
                        :key="`book-${book.id}`"
                        type="button"
                        role="option"
                        :aria-selected="selectedIndex === index ? 'true' : 'false'"
                        @mousedown.prevent
                        @click="selectBook(book)"
                        @mouseenter="selectedIndex = index"
                        :class="[
                            'flex w-full cursor-pointer items-center space-x-3 p-3 text-left transition-colors hover:bg-foxy-elevated',
                            selectedIndex === index ? 'bg-foxy-elevated' : '',
                        ]"
                    >
                        <span class="book-cover-frame h-16 w-12 flex-shrink-0 rounded shadow-sm">
                            <img
                                :src="book.cover_image || bookCoverPlaceholder"
                                :alt="book.title"
                                loading="lazy"
                                decoding="async"
                                width="48"
                                height="64"
                                class="book-cover-media"
                            />
                        </span>
                        <span class="min-w-0 flex-1">
                            <span class="block truncate text-sm font-semibold text-foxy-text">{{ book.title }}</span>
                            <span class="block truncate text-xs text-foxy-text-secondary">{{ book.author }}</span>
                            <span class="mt-1 flex items-center text-xs text-foxy-text-secondary">
                                <svg class="mr-1 h-3 w-3 text-foxy-honey" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                {{ formatRating(book.rating) }}
                            </span>
                        </span>
                    </button>
                </template>
                <div v-else class="p-3 text-sm foxy-muted" role="status">Книг не знайдено</div>

                <template v-if="visibleHashtagSuggestions.length > 0">
                    <div class="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide foxy-muted" role="presentation">Хештеги</div>
                    <button
                        v-for="(hashtag, hashtagIndex) in visibleHashtagSuggestions"
                        :id="optionId(visibleBookSuggestions.length + hashtagIndex)"
                        :key="`hashtag-${hashtag.id}`"
                        type="button"
                        role="option"
                        :aria-selected="selectedIndex === visibleBookSuggestions.length + hashtagIndex ? 'true' : 'false'"
                        @mousedown.prevent
                        @click="selectHashtag(hashtag)"
                        @mouseenter="selectedIndex = visibleBookSuggestions.length + hashtagIndex"
                        :class="[
                            'block w-full cursor-pointer px-3 py-3 text-left text-sm font-semibold text-foxy-text transition-colors hover:bg-foxy-elevated',
                            selectedIndex === visibleBookSuggestions.length + hashtagIndex ? 'bg-foxy-elevated' : '',
                        ]"
                    >
                        #{{ hashtag.name }}
                    </button>
                </template>
            </template>

            <div v-else class="p-3 text-sm foxy-muted" role="status">Нічого не знайдено</div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

let searchInstanceCounter = 0;

export default {
    name: 'BookSearch',
    props: {
        placeholder: {
            type: String,
            default: 'Пошук книг...',
        },
        initialValue: {
            type: String,
            default: '',
        },
    },
    data() {
        const instanceId = `global-search-${++searchInstanceCounter}`;

        return {
            searchQuery: this.initialValue || '',
            bookSuggestions: [],
            hashtagSuggestions: [],
            showSuggestions: false,
            selectedIndex: -1,
            searchTimeout: null,
            blurTimeout: null,
            requestGeneration: 0,
            loading: false,
            hasSearched: false,
            inputId: `${instanceId}-input`,
            listboxId: `${instanceId}-listbox`,
            bookCoverPlaceholder:
                window.imagePlaceholders && window.imagePlaceholders.bookCover
                    ? window.imagePlaceholders.bookCover
                    : '/images/placeholders/book-cover.svg',
        };
    },
    computed: {
        explicitHashtagMode() {
            return this.searchQuery.trim().startsWith('#');
        },
        visibleBookSuggestions() {
            return this.explicitHashtagMode ? [] : this.bookSuggestions.slice(0, 5);
        },
        visibleHashtagSuggestions() {
            const limit = this.explicitHashtagMode ? 7 : Math.min(3, 7 - this.visibleBookSuggestions.length);
            return this.hashtagSuggestions.slice(0, limit);
        },
        selectableSuggestions() {
            return [
                ...this.visibleBookSuggestions.map(item => ({ type: 'book', item })),
                ...this.visibleHashtagSuggestions.map(item => ({ type: 'hashtag', item })),
            ];
        },
        hasAnyNormalResults() {
            return this.visibleBookSuggestions.length > 0 || this.visibleHashtagSuggestions.length > 0;
        },
        activeDescendant() {
            return this.showSuggestions && this.selectedIndex >= 0 ? this.optionId(this.selectedIndex) : null;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
        this.requestGeneration += 1;
        this.clearTimers();
    },
    methods: {
        handleInput() {
            this.requestGeneration += 1;
            this.clearSearchTimeout();
            this.bookSuggestions = [];
            this.hashtagSuggestions = [];
            this.selectedIndex = -1;
            this.hasSearched = false;
            this.loading = false;

            const query = this.searchQuery.trim();
            const hashtagQuery = query.startsWith('#') ? query.slice(1).trim() : query;
            const minimumMet = query.startsWith('#') ? hashtagQuery.length >= 1 : query.length >= 2;

            if (!minimumMet) {
                this.showSuggestions = false;
                return;
            }

            this.showSuggestions = true;
            this.searchTimeout = setTimeout(() => this.fetchSuggestions(), 300);
        },
        async fetchSuggestions() {
            const query = this.searchQuery.trim();
            const explicitHashtagMode = query.startsWith('#');
            const hashtagQuery = explicitHashtagMode ? query.slice(1).trim() : query;
            if ((explicitHashtagMode && hashtagQuery.length < 1) || (!explicitHashtagMode && query.length < 2)) {
                return;
            }

            const generation = ++this.requestGeneration;
            this.loading = true;
            this.showSuggestions = true;
            this.selectedIndex = -1;
            this.bookSuggestions = [];
            this.hashtagSuggestions = [];

            const bookRequest = explicitHashtagMode
                ? Promise.resolve([])
                : axios
                      .get('/api/books/search/suggestions', { params: { q: query, limit: 5 } })
                      .then(response => response.data.data || [])
                      .catch(() => []);
            const hashtagRequest = axios
                .get('/api/hashtags/search/suggestions', { params: { q: hashtagQuery } })
                .then(response => response.data.data || [])
                .catch(() => []);

            const [books, hashtags] = await Promise.all([bookRequest, hashtagRequest]);
            if (generation !== this.requestGeneration) {
                return;
            }

            this.bookSuggestions = books;
            this.hashtagSuggestions = hashtags;
            this.loading = false;
            this.hasSearched = true;
            this.showSuggestions = true;
        },
        handleFocus() {
            if (this.blurTimeout) {
                clearTimeout(this.blurTimeout);
                this.blurTimeout = null;
            }
            if (this.loading || this.hasSearched) {
                this.showSuggestions = true;
            }
        },
        handleBlur() {
            this.blurTimeout = setTimeout(() => {
                this.showSuggestions = false;
            }, 200);
        },
        handleClickOutside(event) {
            if (this.$refs.searchContainer && !this.$refs.searchContainer.contains(event.target)) {
                this.showSuggestions = false;
            }
        },
        navigateDown() {
            if (!this.showSuggestions) {
                this.handleFocus();
            }
            if (this.selectableSuggestions.length > 0) {
                this.selectedIndex = Math.min(this.selectedIndex + 1, this.selectableSuggestions.length - 1);
                this.scrollToSelected();
            }
        },
        navigateUp() {
            if (this.selectableSuggestions.length > 0) {
                this.selectedIndex = this.selectedIndex <= 0 ? 0 : this.selectedIndex - 1;
                this.scrollToSelected();
            }
        },
        scrollToSelected() {
            this.$nextTick(() => {
                const selected = document.getElementById(this.optionId(this.selectedIndex));
                if (selected) {
                    selected.scrollIntoView({ block: 'nearest' });
                }
            });
        },
        selectSuggestion() {
            const selected = this.selectableSuggestions[this.selectedIndex];
            if (!selected) {
                this.handleSearch();
                return;
            }

            if (selected.type === 'book') {
                this.selectBook(selected.item);
            } else {
                this.selectHashtag(selected.item);
            }
        },
        selectBook(book) {
            window.location.href = `/books/${book.slug}`;
        },
        selectHashtag(hashtag) {
            window.location.href = hashtag.url || `/hashtags/${hashtag.slug}`;
        },
        handleSearch() {
            const query = this.searchQuery.trim();
            if (query) {
                window.location.href = `/search?q=${encodeURIComponent(query)}`;
            }
        },
        hideSuggestions() {
            this.showSuggestions = false;
            this.selectedIndex = -1;
        },
        optionId(index) {
            return `${this.listboxId}-option-${index}`;
        },
        clearSearchTimeout() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
                this.searchTimeout = null;
            }
        },
        clearTimers() {
            this.clearSearchTimeout();
            if (this.blurTimeout) {
                clearTimeout(this.blurTimeout);
                this.blurTimeout = null;
            }
        },
        formatRating(rating) {
            if (!rating || rating === 0) return '0.0';
            return parseFloat(rating).toFixed(1);
        },
    },
};
</script>
